import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import { Menu } from "primereact/menu";
import { confirmDialog } from "primereact/confirmdialog";
import { InputComponent } from "../InputComponent";
import { TablaDinamica } from "../TablaDinamica";
import * as generic from "../Generico";
import { useLazyParams } from "../../hooks/useLazyParams";
import { ControlSociedad } from "../../models/trust/cliente/ControlSociedad";
import { PnService } from "../../service/trust/certificacion/PnService";
import { Certificacion } from "../../pages/ficha/trust/clientePn/Certificacion";

const TIPO = "fideicomiso";

export const ControlPersona = ({ toast, id, catalogos, permss, soloLectura, siCliente }) => {

    const omodel = new ControlSociedad();
    const defaultValues = generic.funDefault(omodel.model, null);

    const [dialogVisible, setDialogVisible] = useState(false);
    const [dialogControl, setDialogControl] = useState(false);
    const [registroSeleccionado, setRegistroSeleccionado] = useState(null);
    const menuRef = useRef(null);

    const pnSrv = new PnService();
    const tablaControl = useLazyParams({ modelo: omodel.model });

    const { control, formState: { errors }, handleSubmit, reset, setError, getValues, clearErrors } = useForm({ defaultValues });
    const { inText, inDrop } = InputComponent({ omodel, errors, control, permss, id: getValues('id'), soloLectura });
    const [esRuc, setEsRuc] = useState(false);
    const [esCedula, setEsCedula] = useState(false);

    useEffect(() => {
        if (id) loadLazyData(); // Es el id padre de certificacionPj
        // eslint-disable-next-line
    }, [tablaControl.lazyParams, id]);

    const loadLazyData = () => {
        tablaControl.setLoading(true);
        const _params = { ...tablaControl.lazyParams, idp: id, tipo: TIPO };
        pnSrv.getLista({ lazyEvent: JSON.stringify(_params) }).then(res => {
            tablaControl.setTotalRecords(res.data.total);
            tablaControl.setData(res.data.registros);
        }).finally(() => tablaControl.setLoading(false));
    };

    const openNew = () => {
        reset(defaultValues);
        setDialogVisible(true);
    };

    const onChangeTipoId = (e) => {
        setEsRuc(e.value?.cat_id === catalogos?.ruc?.cat_id);
        setEsCedula(e.value?.cat_id === catalogos?.cedula?.cat_id);
        clearErrors('identificacion');
    };

    const hideDialog = () => setDialogVisible(false);

    const onSubmit = (data) => {
        data.idp = id;
        data.tipo = TIPO;

        const request = getValues('id') == null
            ? pnSrv.nuevoFideicomiso(data)
            : pnSrv.editarFideicomiso(data);

        request.then(res => {
            setDialogVisible(false);
            loadLazyData();
            generic.funcSuccess(res, toast);
        }).catch(error => generic.funcError(error, toast, setError));
    };

    const editRegistro = (idReg) => {
        pnSrv.consultarFideicomiso(idReg, { tipo: TIPO }).then(res => {
            const _data = res.data.certificacion;
            reset(generic.renewValues(_data, omodel.model));
            setDialogVisible(true);
        });
    };

    const confirmDelete = (idReg) => {
        confirmDialog({
            message: '¿Está seguro de eliminar esta persona que ejerce el control?',
            header: 'Confirmación',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                pnSrv.eliminarFideicomiso(idReg, { tipo: TIPO, idp: id }).then(res => {
                    loadLazyData();
                    generic.funcSuccess(res, toast);
                }).catch(error => generic.funcDeleteError(error, toast));
            }
        });
    };

    // Condición de solo lectura
    const esSoloLectura = soloLectura || registroSeleccionado?.estado === 'FINALIZADO' || (siCliente && registroSeleccionado?.estado === 'ENVIADO');

    const itemsMenu = () => {
        const items = [];
        const estado = registroSeleccionado?.estado;

        if (permss?.consultar && esSoloLectura) {
            items.push({ label: 'Ver auto-certificación', icon: 'pi pi-eye', command: () => setDialogControl(true) });
        }

        if (!esSoloLectura && permss?.actualizar && (estado === 'BORRADOR' || estado === 'ENVIADO')) {
            items.push({ label: 'Editar persona', icon: 'pi pi-pencil', command: () => editRegistro(registroSeleccionado?.id) });
        }

        if (permss?.actualizar && !esSoloLectura) {
            items.push({ label: 'Llenar auto-certificación', icon: 'pi pi-file-edit', command: () => setDialogControl(true) });
        }

        if (!esSoloLectura && permss?.eliminar && (estado === 'BORRADOR')) {
            items.push({ label: 'Eliminar registro', icon: 'pi pi-trash', command: () => confirmDelete(registroSeleccionado?.id) });
        }

        return [{ label: 'Opciones de Control', items: items }];
    };

    const accionesTemplate = (rowData) => (
        <Button icon="pi pi-caret-down" type="button" className="p-button-rounded p-button-success mr-2" title="Opciones/Acciones" aria-controls="popup_menu_control_persona" aria-haspopup
            onClick={(e) => { setRegistroSeleccionado(rowData); menuRef.current.toggle(e); }} />
    );

    const ColumnaAcciones = (
        <Column header="Acciones" body={accionesTemplate} style={{ width: '4rem', textAlign: 'center' }} frozen alignFrozen="right" />
    );

    const cabeceraTabla = () => (
        <div className="flex align-items-center justify-content-start gap-3 py-1">
            {permss?.crear && id && !soloLectura && (
                <Button icon="pi pi-plus" className="p-button-success p-button-sm" type="button" onClick={openNew} />
            )}
            <span className="text-sm font-semibold text-600 uppercase tracking-wider">
                Listado de Personas que Ejercen el Control
            </span>
        </div>
    );

    const tipoDoc = esRuc ? 'IDEN_RUC' : esCedula ? 'IDEN_CEDULA' : null;
    const reglaValidacionId = (val) => {
        if (!tipoDoc) return true;
        return generic.validarIdentificacionEcuador(val, tipoDoc) || `${esRuc ? 'RUC' : 'Cédula'} incorrecto`;
    };

    return (
        <div className="componente-control-persona">
            <Menu model={itemsMenu()} popup ref={menuRef} id="popup_menu_control_persona" />
            <TablaDinamica modelo={omodel.model} estadoTabla={tablaControl} datos={tablaControl.data} totalRegistros={tablaControl.totalRecords} dataKey="id" acciones={ColumnaAcciones} header={cabeceraTabla()} />

            <Dialog visible={dialogVisible} header={getValues('id') == null ? "Nueva Persona que Ejerce el Control" : "Editar Persona que Ejerce el Control"} modal
                className="p-fluid modal-ssize" onHide={hideDialog} style={{ width: '600px' }}>
                <form onSubmit={(e) => { e.stopPropagation(); handleSubmit(onSubmit)(e); }}>
                    <div className="formgrid grid px-2">

                        {inText('apellidos', { icon: 'pi pi-user' }, 6)}
                        {inText('nombres', { icon: 'pi pi-user' }, 6)}
                        {inDrop('tipo_identificacion', catalogos?.tipo_identificacion, { lab: 'nombre', chn: onChangeTipoId }, 6)}
                        {inText('identificacion', { validate: reglaValidacionId, icon: 'pi pi-id-card' }, 6)}

                        <div className="col-12 mt-4 flex justify-content-end">
                            {soloLectura ? (
                                <div className="field col">
                                    <Button label="Cerrar" style={{ maxWidth: '120px' }} icon="pi pi-times" type="button" className="p-button-warning" onClick={hideDialog} />
                                </div>
                            ) : (
                                generic.buttonsForms(hideDialog)
                            )}
                        </div>

                    </div>
                </form>
            </Dialog>

            <Dialog visible={dialogControl} header="Formulario de Auto-certificación - Persona que Ejerce el Control" modal onHide={() => setDialogControl(false)} style={{ width: '95vw', maxWidth: '1100px' }}>
                <Certificacion toast={toast} id={registroSeleccionado?.id} idp={id} permss={permss} catalogos={catalogos} soloLectura={esSoloLectura} tipo={TIPO} handlePrev={() => { setDialogControl(false); tablaControl.refreshData(); }} siCliente={siCliente} />
            </Dialog>
        </div>
    );
};
